import type React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { GalleryMinimalisticBroken } from 'solar-icons'
import { cn } from '@/lib/utils'

import { Skeleton } from '@/components/ui/skeleton'

const thumbnailVariants = cva(
  'flex items-center justify-center rounded-lg shrink-0 overflow-hidden bg-gray-100',
  {
    variants: {
      variant: {
        default: '',
      },
      size: {
        xs: 'size-[36px]',
        sm: 'size-[50px]',
        md: 'size-[100px]',
        lg: 'w-full h-[140px] md:size-[200px]',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'sm',
    },
  },
)

export interface ThumbnailProps
  extends React.ComponentPropsWithoutRef<'div'>,
    VariantProps<typeof thumbnailVariants> {
  alt?: string
  src?: string
  iconClassName?: string
  imageClassName?: string
}

export const Thumbnail = ({
  className,
  alt,
  src,
  size,
  variant,
  iconClassName,
  imageClassName,
}: ThumbnailProps) => {
  return (
    <div
      className={cn(thumbnailVariants({ variant, size, className }), {
        'bg-gray-100': !src,
      })}
    >
      {src ? (
        <img
          src={src}
          alt={alt}
          className={cn(
            'size-full object-contain object-[center_bottom]',
            imageClassName,
          )}
        />
      ) : (
        <GalleryMinimalisticBroken
          className={cn(
            'size-6 text-gray-400/60',
            {
              'size-24': size === 'lg',
              'size-16': size === 'md',
              'size-5': size === 'xs',
            },
            iconClassName,
          )}
          strokeWidth={size === 'lg' ? 0.6 : size === 'md' ? 0.6 : 1.5}
        />
      )}
    </div>
  )
}

export const ThumbnailSkeleton = ({ size, variant }: ThumbnailProps) => {
  return (
    <div
      className={cn(thumbnailVariants({ variant, size }), 'border-transparent')}
    >
      <Skeleton className="size-full" />
    </div>
  )
}
